// src/Homepage/HomePage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FileText, Inbox, ListChecks, History } from "lucide-react";
import { useUser } from "../Context/UserContext";
import PageLayout from "../components/layout/PageLayout";
import LatestTenders from "./LatestTenders";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

const WHATSAPP_LINK =
  "https://wa.me/51939560222?text=Hola%20Procu,%20quiero%20contactarte%20para%20hacerte%20estas%20preguntas...";

const HeroBanner = ({ user, onNavigate }) => {
  const slides = [
    {
      title: (
        <>
          Licitaciones privadas, <span className="text-primary">sin fricción</span>
        </>
      ),
      description: "Publica, compara y decide — todo en un solo lugar.",
      ctaLabel: user ? "Crear licitación" : "Regístrate gratis",
      onCta: () => onNavigate(user ? "/create-tender-step-1" : "/register"),
      image: `${process.env.PUBLIC_URL}/fotos/home_b2b.jpg`,
      imageAlt: "Oportunidades B2B",
    },
    {
      title: (
        <>
          Que las licitaciones <span className="text-primary">te encuentren a ti</span>
        </>
      ),
      description: "Súmate al catálogo de proveedores y recibe oportunidades directas.",
      ctaLabel: "Ver catálogo",
      onCta: () => onNavigate("/catalogo"),
      image: `${process.env.PUBLIC_URL}/fotos/perfil_empresa.png`,
      imageAlt: "Catálogo de proveedores",
    },
    {
      title: (
        <>
          ¿Prefieres hablar <span className="text-primary">directo</span>?
        </>
      ),
      description: "Escríbenos por WhatsApp, sin formularios.",
      ctaLabel: "Escríbenos por WhatsApp",
      onCta: () => window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer"),
      image: `${process.env.PUBLIC_URL}/fotos/banner-contacto.jpg`,
      imageAlt: "Contáctanos por WhatsApp",
    },
  ];

  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const goToSlide = (index) => {
    if (index === activeSlide) return;
    setIsVisible(false);
    setTimeout(() => {
      setActiveSlide(index);
      setIsVisible(true);
    }, 400);
  };

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setActiveSlide((prev) => (prev + 1) % slides.length);
        setIsVisible(true);
      }, 400);
    }, 6000);
    return () => clearInterval(interval);
  }, [isPaused, slides.length]);

  const slide = slides[activeSlide];

  return (
    <section
      className="mx-auto max-w-6xl px-4 py-16 sm:px-6"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div
        className={cn(
          "flex flex-col items-center gap-10 lg:flex-row transition-opacity duration-500 ease-in-out",
          isVisible ? "opacity-100" : "opacity-0"
        )}
      >
        <div className="flex-1">
          <img
            src={slide.image}
            alt={slide.imageAlt}
            className="w-full rounded-lg"
          />
        </div>
        <div className="flex-1 space-y-4">
          <h1 className="text-4xl font-bold text-foreground sm:text-5xl lg:text-6xl">
            {slide.title}
          </h1>
          <p className="text-lg text-muted-foreground">{slide.description}</p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={slide.onCta}>{slide.ctaLabel}</Button>
            {!user && (
              <Button variant="outline" onClick={() => onNavigate("/licitaciones")}>
                Ver licitaciones
              </Button>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            {slides.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Ir al slide ${index + 1}`}
                onClick={() => goToSlide(index)}
                className={cn(
                  "h-2 w-2 rounded-full transition-all",
                  index === activeSlide ? "w-6 bg-primary" : "bg-muted"
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const steps = [
  {
    number: 1,
    icon: FileText,
    title: "Publica tu necesidad",
    description:
      "Describe qué buscas en un formulario simple. Toda la comunicación con tus proveedores queda centralizada aquí, no dispersa en chats y correos sueltos.",
  },
  {
    number: 2,
    icon: Inbox,
    title: "Recibe postulaciones",
    description:
      "Proveedores verificados te envían sus propuestas directo a tu licitación, con toda la información que necesitas para decidir.",
  },
  {
    number: 3,
    icon: ListChecks,
    title: "Compara y evalúa rápido",
    description:
      "Revisa todas las postulaciones lado a lado, con perfil y reseñas de cada empresa, y elige en minutos en vez de días.",
  },
  {
    number: 4,
    icon: History,
    title: "Elige y queda todo registrado",
    description:
      "Tu decisión y todo el historial de la licitación quedan guardados: trazabilidad completa para futuras referencias.",
  },
];

const KeyPoint = ({
  title,
  description,
  ctaLabel,
  onCta,
  image,
  imageAlt,
  reverse,
}) => (
  <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
    <div
      className={`flex flex-col items-center gap-10 lg:flex-row ${
        reverse ? "lg:flex-row-reverse" : ""
      }`}
    >
      <div className="flex-1 space-y-4">
        <h3 className="text-2xl font-semibold text-foreground">{title}</h3>
        <p className="text-muted-foreground">{description}</p>
        {ctaLabel && (
          <Button onClick={onCta} className="mt-2">
            {ctaLabel}
          </Button>
        )}
      </div>
      <div className="flex-1">
        <img
          src={image}
          alt={imageAlt}
          className="w-full rounded-lg border border-border"
        />
      </div>
    </div>
  </section>
);

const HomePage = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const publicPaths = ["/register", "/licitaciones"];

  const handleNavigation = (path) => {
    if ((user === null || user === undefined || !user) && !publicPaths.includes(path)) {
      navigate("/login");
    } else {
      navigate(path);
    }
  };

  return (
    <PageLayout>
      <Helmet>
        <title>Procu - Encuentra Proveedores y Oportunidades de Negocio</title>
        <meta
          name="description"
          content="Procu es la mejor plataforma para conectar empresas con proveedores confiables y gestionar licitaciones. Regístrate y accede a oportunidades de negocio."
        />
        <meta
          name="keywords"
          content="Procu, proveedores, negocios, licitaciones, B2B, compras, empresas"
        />
      </Helmet>

      {/* Hero */}
      <HeroBanner user={user} onNavigate={handleNavigation} />

      <section className="border-t border-border bg-muted/40 py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-foreground">
              Así funciona Procu
            </h2>
            <p className="mt-2 text-muted-foreground">
              Toda tu contratación de proveedores, en un solo lugar.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step) => (
              <div
                key={step.number}
                className="flex flex-col items-center gap-3 rounded-lg border border-border bg-background p-6 text-center"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <step.icon className="h-6 w-6" />
                </div>
                <span className="text-sm font-semibold text-primary">
                  Paso {step.number}
                </span>
                <h3 className="text-lg font-semibold text-foreground">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LatestTenders />

      <KeyPoint
        title="Encuentra proveedores confiables en minutos"
        description={
          <>
            Explora nuestro{" "}
            <strong className="text-foreground">catálogo de proveedores</strong>{" "}
            y conecta con empresas listas para ofrecer{" "}
            <strong className="text-foreground">productos y servicios</strong>{" "}
            adaptados a tus necesidades.
          </>
        }
        ctaLabel="Catálogo de Proveedores"
        onCta={() => handleNavigation("/catalogo")}
        image={`${process.env.PUBLIC_URL}/fotos/perfil_empresa.png`}
        imageAlt="Crea tu perfil de empresa"
      />

      <KeyPoint
        title="Convocatorias y Licitaciones"
        description={
          <>
            Publica tus necesidades de servicio a través de{" "}
            <strong className="text-foreground">fáciles formularios</strong>.
            Conecta con proveedores{" "}
            <strong className="text-foreground">verificados</strong> y optimiza
            el proceso de contratación.
          </>
        }
        ctaLabel="Crear Licitación Nueva"
        onCta={() => handleNavigation("/create-tender-step-1")}
        image={`${process.env.PUBLIC_URL}/fotos/licitaciones.png`}
        imageAlt="Participa en licitaciones"
        reverse
      />

      <KeyPoint
        title="Reseñas y comentarios"
        description={
          <>
            <strong className="text-foreground">Comparte tu experiencia</strong>{" "}
            con proveedores y califica sus servicios. Ayuda a la comunidad a
            tomar decisiones basadas en{" "}
            <strong className="text-foreground">
              opiniones de clientes reales
            </strong>
            .
          </>
        }
        ctaLabel="Reseñas"
        onCta={() => handleNavigation("/catalogo")}
        image={`${process.env.PUBLIC_URL}/fotos/puntuaciones.png`}
        imageAlt="Participa en licitaciones"
      />

      <section
        className="relative overflow-hidden py-20"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/fotos/banner-contacto.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="max-w-xl space-y-4 rounded-lg bg-background/90 p-8 shadow-sm backdrop-blur-sm">
            <h3 className="text-2xl font-semibold text-foreground">
              ¿Dudas? ¡Contáctanos!
            </h3>
            <p className="text-muted-foreground">
              Somos un equipo de{" "}
              <strong className="text-foreground">
                emprendedores peruanos
              </strong>{" "}
              que busca revolucionar la forma de hacer{" "}
              <strong className="text-foreground">
                negocios entre empresas.
              </strong>{" "}
              Escríbenos si tienes dudas o sugerencias{" "}
              <strong className="text-foreground">
                (dale click al ícono de WhatsApp)
              </strong>
              .
            </p>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img
                src={`${process.env.PUBLIC_URL}/fotos/whatsapp.png`}
                alt="WhatsApp"
                className="h-12 w-12"
              />
            </a>
            <p className="text-sm text-muted-foreground">
              O escríbenos a{" "}
              <a href="mailto:procuperu1@gmail.com" className="font-medium text-foreground hover:underline">
                procuperu1@gmail.com
              </a>{" "}
              / llámanos al{" "}
              <a href="tel:+51939560222" className="font-medium text-foreground hover:underline">
                939 560 222
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4 text-center sm:px-6">
          <h2 className="text-2xl font-semibold text-foreground">
            Industrias con las que trabajamos
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {[
              {
                img: "limpieza.png",
                alt: "Mantenimiento y Limpieza Industrial",
                label: "Mantenimiento y Limpieza Industrial",
              },
              {
                img: "logistica.png",
                alt: "Servicios de Transporte y Logística",
                label: "Transporte de Carga",
              },
              {
                img: "marketing.png",
                alt: "Servicios de marketing y ventas",
                label: "Marketing y Publicidad",
              },
            ].map((industry) => (
              <button
                key={industry.img}
                onClick={() => handleNavigation("/catalogo")}
                className="flex flex-col items-center gap-3 rounded-lg border border-border p-6 transition-colors hover:bg-muted"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/fotos/${industry.img}`}
                  alt={industry.alt}
                  className="h-16 w-16 object-contain"
                />
                <span className="text-sm font-medium text-foreground">
                  {industry.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default HomePage;
